import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminTitle } from "@/pages/admin/AdminLayout";
import { useUpload } from "@/pages/admin/Courses";
import { Spinner } from "@/components/site/Bits";
import { useApi, errMsg, fileUrl } from "@/lib/api";
import { useSite } from "@/lib/site";

const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;

const ListEditor = ({ items, fields, onChange, testId }) => (
  <div className="space-y-3" data-testid={testId}>
    {items.map((it, i) => (
      <div key={i} className="card-lux !transform-none p-4 grid gap-2 relative">
        {fields.map(([k, l, area]) => area
          ? <Textarea key={k} rows={2} placeholder={l} value={it[k] || ""} onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))} />
          : <Input key={k} placeholder={l} value={it[k] || ""} onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))} />)}
        <button className="absolute top-2 left-2 text-red-600 p-1" onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`${testId}-remove-${i}`}><Trash2 size={15} /></button>
      </div>
    ))}
    <button className="btn-gold-outline !py-2 text-sm" onClick={() => onChange([...items, {}])} data-testid={`${testId}-add`}><Plus size={15} />إضافة</button>
  </div>
);

const ImageField = ({ label, value, onChange, testId }) => {
  const upload = useUpload();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { onChange(await upload(f)); toast.success("تم رفع الصورة"); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <F label={label}>
      <div className="flex items-center gap-3">
        {value && <img src={fileUrl(value)} alt="" className="h-14 rounded-lg border bg-[#FAF7F2]" />}
        <label className="btn-gold-outline !py-2 !px-4 text-sm cursor-pointer"><Upload size={15} />رفع<input type="file" accept="image/*" hidden onChange={onFile} data-testid={testId} /></label>
      </div>
    </F>
  );
};

export default function Content() {
  const api = useApi();
  const { settings, reload } = useSite();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (settings) setS(JSON.parse(JSON.stringify(settings)));
  }, [settings]);
  if (!s) return <Spinner />;
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const setIn = (g, k) => (e) => setS({ ...s, [g]: { ...s[g], [k]: e.target.value } });
  const save = async () => {
    setBusy(true);
    try { const { id, _id, ...data } = s; await api("put", "/admin/settings", { data }); await reload(); toast.success("تم حفظ محتوى الموقع"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="admin-content">
      <AdminTitle title="إدارة محتوى الموقع"><button disabled={busy} onClick={save} className="btn-copper !py-2.5" data-testid="content-save-btn"><Save size={16} />{busy ? "جارٍ الحفظ..." : "حفظ التغييرات"}</button></AdminTitle>
      <Tabs defaultValue="identity" dir="rtl">
        <TabsList className="flex-wrap h-auto bg-white border p-1 mb-6">
          {[["identity", "الهوية والنصوص"], ["contact", "التواصل"], ["features", "المميزات"], ["faqs", "الأسئلة الشائعة"], ["testimonials", "آراء المتدربين"], ["legal", "الصفحات القانونية"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`content-tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="identity" className="grid md:grid-cols-2 gap-4">
          <F label="اسم الموقع"><Input value={s.site_name} onChange={set("site_name")} data-testid="content-site-name" /></F>
          <F label="العبارة التسويقية"><Input value={s.slogan} onChange={set("slogan")} data-testid="content-slogan" /></F>
          <F label="العنوان الرئيسي"><Input value={s.hero_title} onChange={set("hero_title")} data-testid="content-hero-title" /></F>
          <F label="الوصف الرئيسي"><Textarea rows={3} value={s.hero_subtitle} onChange={set("hero_subtitle")} data-testid="content-hero-subtitle" /></F>
          <ImageField label="الشعار" value={s.logo_url} onChange={(v) => setS({ ...s, logo_url: v })} testId="content-logo-upload" />
          <ImageField label="صورة الواجهة الرئيسية" value={s.hero_image} onChange={(v) => setS({ ...s, hero_image: v })} testId="content-hero-upload" />
          <F label="اللون المساند الأساسي (نحاسي)"><Input type="color" value={s.accent_primary} onChange={set("accent_primary")} className="h-11 w-28" data-testid="content-color-primary" /></F>
          <F label="اللون المساند الثانوي (ذهبي)"><Input type="color" value={s.accent_secondary} onChange={set("accent_secondary")} className="h-11 w-28" data-testid="content-color-secondary" /></F>
          <F label="من نحن"><Textarea rows={5} value={s.about_text} onChange={set("about_text")} data-testid="content-about" /></F>
          <div className="space-y-4"><F label="الرؤية"><Textarea rows={2} value={s.vision} onChange={set("vision")} /></F><F label="الرسالة"><Textarea rows={2} value={s.mission} onChange={set("mission")} /></F></div>
        </TabsContent>
        <TabsContent value="contact" className="grid md:grid-cols-2 gap-4">
          {[["phone", "الهاتف"], ["whatsapp", "واتساب"], ["email", "البريد الإلكتروني"], ["address", "العنوان"]].map(([k, l]) => (
            <F key={k} label={l}><Input value={s.contact?.[k] || ""} onChange={setIn("contact", k)} data-testid={`content-contact-${k}`} /></F>
          ))}
          {[["instagram", "إنستغرام"], ["x", "X (تويتر)"], ["youtube", "يوتيوب"], ["tiktok", "تيك توك"], ["linkedin", "لينكدإن"], ["snapchat", "سناب شات"]].map(([k, l]) => (
            <F key={k} label={`رابط ${l}`}><Input dir="ltr" value={s.social?.[k] || ""} onChange={setIn("social", k)} placeholder="https://" data-testid={`content-social-${k}`} /></F>
          ))}
        </TabsContent>
        <TabsContent value="features"><ListEditor testId="content-features" items={s.features || []} fields={[["title", "العنوان"], ["text", "الوصف", true]]} onChange={(v) => setS({ ...s, features: v })} /></TabsContent>
        <TabsContent value="faqs"><ListEditor testId="content-faqs" items={s.faqs || []} fields={[["q", "السؤال"], ["a", "الإجابة", true]]} onChange={(v) => setS({ ...s, faqs: v })} /></TabsContent>
        <TabsContent value="testimonials"><ListEditor testId="content-testimonials" items={s.testimonials || []} fields={[["name", "اسم المتدرب"], ["role", "الصفة"], ["text", "الرأي", true]]} onChange={(v) => setS({ ...s, testimonials: v })} /></TabsContent>
        <TabsContent value="legal" className="space-y-4">
          <F label="سياسة الخصوصية"><Textarea rows={7} value={s.privacy} onChange={set("privacy")} data-testid="content-privacy" /></F>
          <F label="شروط الاستخدام"><Textarea rows={7} value={s.terms} onChange={set("terms")} data-testid="content-terms" /></F>
          <F label="سياسة الإلغاء والاسترداد"><Textarea rows={7} value={s.refund} onChange={set("refund")} data-testid="content-refund" /></F>
        </TabsContent>
      </Tabs>
    </div>
  );
}
